import { useState } from "react";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import SpeakButton from "@/components/SpeakButton";
import BeyondAwakePanels from "@/components/BeyondAwakePanels";
import SpokenContract, {
  appendContractToPdf,
} from "@/components/SpokenContract";

const SECTIONS = [
  { key: "days_30", label: "30 Days", subtitle: "What you will test", color: "#4aa0a4" },
  { key: "days_60", label: "60 Days", subtitle: "What you will build", color: "#f4b847" },
  { key: "days_90", label: "90 Days", subtitle: "What you will commit to", color: "#3c8386" },
];

function PlanContent({ plan, refinedProblem, attention, witness }) {
  return (
    <motion.div
      key="plan-view"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="plan-content"
    >
      {/* Problem statement */}
      <div className="mt-8 weave-card-soft" data-testid="output-problem">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">
          Problem statement
        </div>
        <p className="text-xl font-light text-slate-800 leading-relaxed">
          "{refinedProblem}"
        </p>
      </div>

      {/* Idea + audience recall */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="weave-card" data-testid="output-idea">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">
            The idea
          </div>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">
            {attention.working_on}
          </p>
        </div>
        <div className="weave-card" data-testid="output-audience">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">
            Who it's for
          </div>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">
            {witness.who_for}
          </p>
        </div>
      </div>

      <div className="weave-divider" />

      {/* 30 / 60 / 90 */}
      <div className="space-y-4">
        {SECTIONS.map((s) => (
          <div
            key={s.key}
            className="weave-card relative overflow-hidden"
            data-testid={`output-${s.key}`}
          >
            <span
              className="absolute left-0 top-0 bottom-0"
              style={{ width: 3, background: s.color, opacity: 0.5 }}
              aria-hidden="true"
            />
            <div className="flex items-baseline justify-between mb-3 gap-3">
              <h3
                className="text-xl font-medium tracking-tight"
                style={{ color: "#1e293b" }}
              >
                {s.label}
              </h3>
              <div className="flex items-center gap-3">
                <span
                  className="text-xs uppercase tracking-[0.18em]"
                  style={{ color: s.color }}
                >
                  {s.subtitle}
                </span>
                <SpeakButton
                  text={`${s.label}. ${s.subtitle}. ${plan[s.key]}`}
                  label={`Read ${s.label} aloud`}
                  testid={`output-speak-${s.key}`}
                />
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">
              {plan[s.key]}
            </p>
          </div>
        ))}
      </div>

      {/* Where this might fail */}
      <div
        className="mt-6 rounded-2xl border p-7"
        style={{
          background: "#fff7f6",
          borderColor: "rgba(233,62,54,0.22)",
        }}
        data-testid="output-might-fail"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span
              className="weave-dot"
              style={{ background: "#e93e36" }}
              aria-hidden="true"
            />
            <span
              className="text-xs uppercase tracking-[0.18em] font-medium"
              style={{ color: "#b8332c" }}
            >
              Where this might fail
            </span>
          </div>
          <SpeakButton
            text={`Where this might fail. ${plan.might_fail}`}
            label="Read the risks aloud"
            testid="output-speak-might-fail"
          />
        </div>
        <p
          className="leading-relaxed whitespace-pre-line"
          style={{ color: "#7a221c" }}
        >
          {plan.might_fail}
        </p>
      </div>
    </motion.div>
  );
}

function buildPdf({ plan, refinedProblem, attention, witness }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 56;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeWrapped = (text, options = {}) => {
    const {
      size = 11,
      lineHeight = 16,
      color = [30, 41, 59],
      style = "normal",
      gapAfter = 8,
    } = options;
    if (!text) return;
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text, contentWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    });
    y += gapAfter;
  };

  const writeRule = (color = [226, 232, 240]) => {
    ensureSpace(20);
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 18;
  };

  const writeSectionHeader = (label, accent) => {
    ensureSpace(40);
    doc.setFillColor(accent[0], accent[1], accent[2]);
    doc.circle(margin + 3, y - 3, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(60, 131, 134);
    doc.text(label.toUpperCase(), margin + 14, y);
    y += 18;
  };

  // ----- Cover -----
  // Brand mark
  doc.setFillColor(74, 160, 164);
  doc.circle(margin + 4, margin + 4, 4, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 131, 134);
  doc.text("THE WEAVE  ·  STAY AWAKE", margin + 16, margin + 8);
  y = margin + 56;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(28);
  doc.setTextColor(30, 41, 59);
  doc.text("Your 90-Day Plan", margin, y);
  y += 28;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  const stamp = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Drafted ${stamp}`, margin, y);
  y += 28;

  writeRule();

  // ----- Refined problem -----
  writeSectionHeader("Problem statement", [74, 160, 164]);
  writeWrapped(refinedProblem, { size: 13, lineHeight: 18, gapAfter: 18 });

  // ----- Idea recall -----
  writeSectionHeader("The idea", [244, 184, 71]);
  writeWrapped(attention.working_on, { size: 11, lineHeight: 16, gapAfter: 14 });

  writeSectionHeader("Who it's for", [244, 184, 71]);
  writeWrapped(witness.who_for, { size: 11, lineHeight: 16, gapAfter: 22 });

  writeRule();

  // ----- 30 / 60 / 90 -----
  SECTIONS.forEach((s) => {
    const accent =
      s.color === "#4aa0a4"
        ? [74, 160, 164]
        : s.color === "#f4b847"
        ? [244, 184, 71]
        : [60, 131, 134];
    ensureSpace(60);
    writeSectionHeader(`${s.label}  ·  ${s.subtitle}`, accent);
    writeWrapped(plan[s.key], { size: 11, lineHeight: 17, gapAfter: 18 });
  });

  writeRule([233, 62, 54]);
  writeSectionHeader("Where this might fail", [233, 62, 54]);
  writeWrapped(plan.might_fail, {
    size: 11,
    lineHeight: 17,
    color: [148, 28, 22],
    gapAfter: 24,
  });

  // Footer line on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      "wearetheweave.co.uk  ·  Stay AWAKE",
      margin,
      pageHeight - 28
    );
    doc.text(`${i} / ${pageCount}`, pageWidth - margin, pageHeight - 28, {
      align: "right",
    });
  }

  return doc;
}

export default function PlanOutput({
  plan,
  refinedProblem,
  attention,
  witness,
  firstName,
  onRestart,
}) {
  const [view, setView] = useState("plan"); // "plan" | "contract"

  const downloadPdf = () => {
    try {
      const doc = buildPdf({ plan, refinedProblem, attention, witness });
      appendContractToPdf(doc, { plan, refinedProblem, firstName });
      doc.save("stay-awake-90-day-plan.pdf");
    } catch (e) {
      toast.error("Couldn't build the PDF", {
        description: e?.message || "Please try again.",
      });
    }
  };

  const fullPlanText = [
    `Your 90-day plan.`,
    `Problem statement: ${refinedProblem}.`,
    `30 days, what you will test: ${plan.days_30}.`,
    `60 days, what you will build: ${plan.days_60}.`,
    `90 days, what you will commit to: ${plan.days_90}.`,
    `Where this might fail: ${plan.might_fail}.`,
  ].join(" ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="plan-output"
    >
      <div
        className="weave-step-pill"
        style={{
          borderColor: "#4aa0a440",
          background: "#4aa0a410",
          color: "#3c8386",
        }}
      >
        <span
          className="weave-dot"
          style={{ background: "#4aa0a4" }}
          aria-hidden="true"
        />
        Your plan
      </div>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <h2 className="text-4xl md:text-5xl font-light text-slate-800 tracking-tight leading-tight">
          Your 90-Day Plan
        </h2>
        <SpeakButton
          text={fullPlanText}
          label="Read the entire plan aloud"
          testid="plan-speak-all-btn"
        />
      </div>

      <p className="mt-4 text-slate-500 text-base">
        A clearer idea, a sharper problem, and three honest commitments.
      </p>

      <div className="mt-8" data-testid="plan-tab-toggle-wrap">
        <div className="weave-tab-toggle" role="tablist" aria-label="Output view">
          <button
            type="button"
            role="tab"
            aria-selected={view === "plan"}
            className={view === "plan" ? "is-active" : ""}
            onClick={() => setView("plan")}
            data-testid="tab-plan"
          >
            The plan
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "contract"}
            className={view === "contract" ? "is-active" : ""}
            onClick={() => setView("contract")}
            data-testid="tab-contract"
          >
            Your spoken contract
          </button>
        </div>
      </div>

      {view === "contract" && (
        <SpokenContract
          plan={plan}
          refinedProblem={refinedProblem}
          firstName={firstName}
        />
      )}

      {view === "plan" && (
        <PlanContent
          plan={plan}
          refinedProblem={refinedProblem}
          attention={attention}
          witness={witness}
        />
      )}

      <div className="mt-12 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={downloadPdf}
          className="weave-btn-primary"
          data-testid="download-pdf-btn"
        >
          Download as PDF
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="weave-btn-outline"
          data-testid="restart-output-btn"
        >
          Start a new journey
        </button>
      </div>

      <p className="mt-10 text-sm text-slate-400 max-w-xl">
        Stay AWAKE doesn't store your responses. When you close this page or
        start over, the plan goes with you only as the PDF you've saved.
      </p>

      <div className="mt-12">
        <BeyondAwakePanels />
      </div>
    </motion.div>
  );
}
